'use client'

import * as React from 'react'
import { PencilLine } from 'lucide-react'
import { AI_NON_AI_FALLBACK_COPY } from '@/lib/ai/quota-copy'
import { cn } from '@/lib/utils'

/**
 * "This summary was written without AI." — the notice the feedback widget was
 * missing (Plan Phase 6c).
 *
 * ── Why this is a component and not two lines inline ────────────────────────
 * The widget renders its review panel in one 150-line JSX block, and the
 * degradation signal needs to sit inside it without being lost in the next
 * edit. More importantly, a silent degrade is forbidden by rule, and a rule
 * that lives as an `&&` in the middle of a large component is one refactor
 * away from disappearing. A named component with a `data-testid` is something
 * a test can hold on to.
 *
 * ── Deliberately not an <Alert variant="destructive"> ───────────────────────
 * Nothing failed from the reporter's point of view: their ticket is about to be
 * filed with their own words, which is a perfectly good outcome. The tone
 * follows `components/ai/ai-degraded-notice.tsx` — state the limitation
 * plainly, do not apologise, do not explain the machinery. No provider, model
 * or status appears, because the copy comes from `lib/ai/quota-copy.ts` and
 * this component composes no sentences of its own.
 *
 * ── Renders nothing when AI DID run ─────────────────────────────────────────
 * Silence is success. A "this was AI-generated" badge on every normal summary
 * is noise that trains people to ignore the one time it matters.
 */
export interface FeedbackAiFallbackNoticeProps {
  /** The `aiGenerated` flag from the summary — false ⇒ the notice shows. */
  aiGenerated: boolean
  className?: string
}

export function FeedbackAiFallbackNotice({
  aiGenerated,
  className,
}: FeedbackAiFallbackNoticeProps) {
  if (aiGenerated) return null

  return (
    <div
      data-testid="feedback-ai-fallback-notice"
      className={cn(
        'flex items-start gap-2 rounded-md border border-amber-500/25 bg-amber-500/10 px-2.5 py-2',
        className,
      )}
    >
      <PencilLine
        aria-hidden="true"
        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400"
      />
      <div className="space-y-0.5">
        <p className="text-[11px] font-semibold text-amber-800 dark:text-amber-300">
          {AI_NON_AI_FALLBACK_COPY.headline}
        </p>
        <p className="text-[11px] leading-relaxed text-amber-800/90 dark:text-amber-300/90">
          {AI_NON_AI_FALLBACK_COPY.body}
        </p>
      </div>
    </div>
  )
}
